import { useRef, useState, useEffect, useCallback } from 'react'
import { Plus, Minus } from 'lucide-react'
import { Settings } from '../api'
import { getProfilerRemoteSettings, type RemoteSettingsResult } from '../api_profiler'
import { SectionName, getFieldsForSection, isFieldVisible, ResourceDef, RESOURCES } from '../resources'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import ProxyPresetsPanel from './ProxyPresetsPanel'

interface Props {
  section: SectionName
  cfg: Settings
  update: (patch: Partial<Settings>) => void
}

function HintTooltip({ text }: { text: string }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full text-xs font-bold shrink-0 border border-border text-muted-foreground hover:text-primary hover:border-primary/30 hover:bg-primary/10 transition-all"
          >
            ?
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-[280px] text-xs leading-relaxed">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

function CustomDropdown({ options, value, onChange, optionLabels }: {
  options: string[]
  value: string
  onChange: (v: string) => void
  optionLabels?: Record<string, string>
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close()
    }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    document.addEventListener('mousedown', handler)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('keydown', esc)
    }
  }, [open, close])

  useEffect(() => {
    if (open && listRef.current) {
      const active = listRef.current.querySelector('[data-active="true"]') as HTMLElement
      if (active) active.scrollIntoView({ block: 'nearest' })
    }
  }, [open])

  const display = (value && (optionLabels?.[value] ?? value)) || '\u2014'

  return (
    <div ref={ref} className="relative w-full max-w-80">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between gap-3 rounded-md px-3 py-2 text-sm text-left transition-all bg-background border text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${open ? 'border-ring' : 'border-input'}`}
      >
        <span className="truncate">{display}</span>
        <svg
          width="12" height="12" viewBox="0 0 12 12" fill="none"
          className={`shrink-0 transition-transform duration-150 text-muted-foreground ${open ? 'rotate-180' : ''}`}
        >
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div
          ref={listRef}
          className="absolute left-0 right-0 z-50 mt-1 w-full max-h-56 overflow-y-auto rounded-md py-1 shadow-lg animate-fade-in bg-popover border border-border"
        >
          {options.map(opt => {
            const selected = opt === value
            const label = (opt && (optionLabels?.[opt] ?? opt)) || '\u2014'
            return (
              <button
                key={opt}
                type="button"
                data-active={selected}
                onClick={() => { onChange(opt); close() }}
                className={`w-full text-left px-3 py-1.5 text-sm transition-colors truncate ${selected ? 'text-primary bg-primary/10' : 'text-foreground hover:bg-accent'}`}
              >
                {label}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

function SelectField({ def, value, onChange }: {
  def: ResourceDef
  value: any
  onChange: (v: any) => void
}) {
  const opts = def.options ?? []
  const isCheckbox = opts.length === 2 && opts[0] === '\u0414\u0430' && opts[1] === ''

  if (isCheckbox) {
    const checked = String(value ?? def.defaultValue) === '\u0414\u0430'
    return (
      <div className="flex items-center gap-2.5">
        <Switch checked={checked} onCheckedChange={v => onChange(v ? '\u0414\u0430' : '')} />
        <span className={`text-xs ${checked ? 'text-foreground' : 'text-muted-foreground'}`}>
          {checked ? '\u0414\u0430' : '\u041d\u0435\u0442'}
        </span>
      </div>
    )
  }

  return (
    <CustomDropdown
      options={opts}
      value={String(value ?? def.defaultValue)}
      onChange={onChange}
      optionLabels={def.optionLabels}
    />
  )
}

function LinesFromFileField({ value, description, onChange }: {
  value: any
  description: string
  onChange: (v: string) => void
}) {
  const fileRef = useRef<HTMLInputElement>(null)

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      const text = ev.target?.result as string
      onChange(text)
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  return (
    <div className="space-y-1.5 w-full min-w-0 max-w-full md:max-w-md">
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => fileRef.current?.click()}
          className="gap-1.5 text-xs"
        >
          <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"/>
          </svg>
          Загрузить файл
        </Button>
        <input ref={fileRef} type="file" accept=".txt,.csv,.list" className="hidden" onChange={handleFile}/>
        {value && (
          <span className="text-xs tabular-nums text-muted-foreground">
            {String(value).split('\n').filter(Boolean).length} строк
          </span>
        )}
      </div>
      <Textarea
        value={String(value ?? '')}
        onChange={e => onChange(e.target.value)}
        rows={4}
        placeholder={`${description}\n(по одному значению на строку)`}
        className="w-full resize-y min-h-[80px] text-sm"
      />
    </div>
  )
}

function FilesFromDirectoryField({ value, description, onChange }: {
  value: any
  description: string
  onChange: (v: string) => void
}) {
  return (
    <div className="flex flex-col gap-2 min-w-0 md:flex-row md:items-center">
      <Input
        type="text"
        value={String(value ?? '')}
        onChange={e => onChange(e.target.value)}
        placeholder={`Путь к папке (${description})`}
        className="w-full max-w-80 text-sm min-w-0"
      />
      <Badge variant="secondary" className="text-xs shrink-0 w-fit">DIR</Badge>
    </div>
  )
}

function MultiCheckboxField({ def, value, onChange }: {
  def: ResourceDef
  value: any
  onChange: (v: string) => void
}) {
  const opts = def.options ?? []
  const currentStr = String(value ?? '')
  const active = new Set(currentStr.split(',').map(s => s.trim()).filter(Boolean))

  const toggle = (opt: string) => {
    const next = new Set(active)
    if (next.has(opt)) next.delete(opt)
    else next.add(opt)
    onChange(Array.from(next).join(','))
  }

  return (
    <div className="flex flex-col gap-2 w-full max-w-md">
      {opts.map(opt => {
        const checked = active.has(opt)
        return (
          <Label
            key={opt}
            htmlFor={`mc-${opt}`}
            className={`group flex items-center gap-3 rounded-lg border px-3.5 py-2.5 cursor-pointer transition-colors select-none
              ${checked
                ? 'border-primary/40 bg-primary/5 hover:bg-primary/10'
                : 'border-border bg-card hover:bg-accent/40'}`}
          >
            <Checkbox
              id={`mc-${opt}`}
              checked={checked}
              onCheckedChange={() => toggle(opt)}
              className="shrink-0"
            />
            <span className={`flex-1 text-sm ${checked ? 'text-foreground font-medium' : 'text-foreground/80'}`}>
              {opt}
            </span>
          </Label>
        )
      })}
    </div>
  )
}

function NumberStepper({ def, value, onChange }: {
  def: ResourceDef
  value: any
  onChange: (v: number) => void
}) {
  const min = def.minValue ?? 0
  const max = def.maxValue ?? 9999
  const n = Number(value ?? def.defaultValue) || 0
  const clamp = (v: number) => Math.max(min, Math.min(max, v))

  const holdRef = useRef<number | null>(null)
  const currentRef = useRef<number>(n)
  currentRef.current = n
  const startHold = (delta: number) => {
    currentRef.current = clamp(currentRef.current + delta)
    onChange(currentRef.current)
    let ticks = 0
    holdRef.current = window.setInterval(() => {
      ticks += 1
      const step = ticks > 8 ? 5 : 1
      currentRef.current = clamp(currentRef.current + delta * step)
      onChange(currentRef.current)
    }, 130)
  }
  const endHold = () => {
    if (holdRef.current) { window.clearInterval(holdRef.current); holdRef.current = null }
  }
  useEffect(() => endHold, [])

  const step = (delta: number) => onChange(clamp(n + delta))
  return (
    <div className="inline-flex items-stretch h-9 rounded-md border border-input bg-background overflow-hidden shadow-xs">
      <button
        type="button"
        onPointerDown={e => { e.preventDefault(); startHold(-1) }}
        onPointerUp={endHold}
        onPointerLeave={endHold}
        onPointerCancel={endHold}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); step(-1) } }}
        disabled={n <= min}
        className="px-3 h-full flex items-center text-muted-foreground hover:text-foreground hover:bg-accent active:bg-accent/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors border-r border-input"
        aria-label="Уменьшить"
      >
        <Minus className="w-4 h-4" />
      </button>
      <Input
        type="number"
        min={min}
        max={max}
        value={n}
        onChange={e => {
          let v = Number(e.target.value)
          if (!Number.isFinite(v)) v = min
          onChange(clamp(v))
        }}
        className="w-20 h-full text-center tabular-nums border-0 rounded-none focus-visible:ring-0 shadow-none"
      />
      <button
        type="button"
        onPointerDown={e => { e.preventDefault(); startHold(1) }}
        onPointerUp={endHold}
        onPointerLeave={endHold}
        onPointerCancel={endHold}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); step(1) } }}
        disabled={n >= max}
        className="px-3 h-full flex items-center text-muted-foreground hover:text-foreground hover:bg-accent active:bg-accent/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors border-l border-input"
        aria-label="Увеличить"
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  )
}

function isBoolResource(def: ResourceDef): boolean {
  return def.type === 'Select'
    && Array.isArray(def.options)
    && def.options.length === 2
    && def.options[0] === '\u0414\u0430'
    && def.options[1] === ''
}

function isToggleSelectResource(def: ResourceDef): boolean {
  return def.type === 'Select' && typeof def.toggleOff === 'string'
}

// Root-level "switch" includes bool ("Да"/"") + Select with toggleOff.
function isSwitchResource(def: ResourceDef): boolean {
  return isBoolResource(def) || isToggleSelectResource(def)
}

// Switch-row in shadcn "Description" style:
//  - mobile: Choice Card — bordered clickable card
//  - desktop: inline row — no border/bg, just text left + switch right, divider between rows
// If there are children (dependent fields shown only when switch is on),
// they render inside the card below the header with a subtle indent.
function BoolFieldRow({ def, value, onChange, hasChildren, children }: {
  def: ResourceDef
  value: any
  onChange: (v: any) => void
  hasChildren?: boolean
  children?: React.ReactNode
}) {
  const isToggleSelect = isToggleSelectResource(def)
  const current = String(value ?? def.defaultValue)
  const checked = isToggleSelect ? current !== def.toggleOff : current === '\u0414\u0430'

  // Auto-scroll: when user turns ON a switch that has dependent fields,
  // scroll the whole card into view so the newly revealed children are visible.
  const rootRef = useRef<HTMLDivElement>(null)
  const prevChecked = useRef(checked)
  useEffect(() => {
    if (!prevChecked.current && checked && hasChildren && rootRef.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
          setTimeout(() => {
            const el = rootRef.current
            if (!el) return
            const rect = el.getBoundingClientRect()
            const vh = window.innerHeight || document.documentElement.clientHeight
            if (rect.bottom > vh) {
              el.scrollIntoView({ behavior: 'smooth', block: 'end' })
            }
          }, 220)
        })
      })
    }
    prevChecked.current = checked
  }, [checked, hasChildren])

  const toggle = () => {
    if (isToggleSelect) {
      if (checked) {
        onChange(def.toggleOff)
      } else {
        const firstOn = (def.options ?? []).find(o => o !== def.toggleOff) ?? ''
        onChange(firstOn)
      }
    } else {
      onChange(checked ? '' : '\u0414\u0430')
    }
  }
  // When toggle-select is ON, we show an inline dropdown with the non-off options
  // as the first nested item.
  const showInlineSelect = isToggleSelect && checked
  const nonOffOptions = isToggleSelect ? (def.options ?? []).filter(o => o !== def.toggleOff) : []
  const hasKids = checked && (!!hasChildren || showInlineSelect)
  return (
    <div
      ref={rootRef}
      className={`group transition-colors select-none
        rounded-xl border px-4 py-3 ${hasKids ? 'pb-4' : ''}
        md:rounded-xl md:px-4 md:py-3 ${hasKids ? 'md:pb-4' : ''}
        ${!hasKids ? 'md:border-0 md:border-b md:border-border/30 md:last:border-b-0 md:bg-transparent md:rounded-none md:px-2 md:py-3.5' : ''}
        ${hasKids
          ? 'bg-muted/40 border-border md:bg-muted/40'
          : (checked
              ? 'bg-muted/50 border-border md:bg-transparent'
              : 'bg-card border-border md:bg-transparent')}`}
    >
      <div
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={e => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggle() } }}
        className="flex items-center gap-3 cursor-pointer hover:bg-transparent"
      >
        <div className="flex-1 min-w-0 space-y-0.5">
          <div className={`text-sm leading-snug ${checked ? 'font-semibold text-foreground' : 'font-medium text-foreground'}`}>
            {def.description}
          </div>
          {def.hint && (
            <div className="text-[11px] md:text-xs text-muted-foreground leading-snug">
              {def.hint}
            </div>
          )}
        </div>
        <Switch
          checked={checked}
          onCheckedChange={toggle}
          onClick={e => e.stopPropagation()}
          className="shrink-0"
        />
      </div>
      {hasKids && (
        <div className="mt-3 pt-3 border-t border-border/40 space-y-2.5">
          {showInlineSelect && (
            <div className="flex flex-col gap-1.5 md:flex-row md:items-center md:gap-3 py-1.5">
              <label className="text-[12px] md:text-[13px] leading-snug font-medium text-foreground md:w-56 md:shrink-0 flex items-center gap-1.5">
                <span>Режим</span>
              </label>
              <div className="min-w-0 flex-1">
                <CustomDropdown
                  options={nonOffOptions}
                  value={current}
                  onChange={v => onChange(v)}
                />
              </div>
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  )
}

function RemoteSettingsField({ value, onChange, def }: {
  value: any
  onChange: (v: any) => void
  def: ResourceDef
}) {
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<RemoteSettingsResult | null>(null)
  const id = String(value ?? '').trim()

  const fetchSettings = useCallback(async () => {
    if (!id) {
      setResult({ ok: false, error: 'Введите идентификатор удалённой работы' })
      return
    }
    setLoading(true)
    setResult(null)
    try {
      const r = await getProfilerRemoteSettings(id)
      setResult(r)
    } finally {
      setLoading(false)
    }
  }, [id])

  const mappedEntries = result?.ok && result.mapped ? Object.entries(result.mapped) : []
  const rawEntries = result?.ok && result.raw ? Object.entries(result.raw) : []

  return (
    <div className="flex flex-col gap-2 min-w-0">
      <div className="flex items-center gap-2 min-w-0">
        <Input
          value={String(value ?? '')}
          onChange={e => onChange(e.target.value)}
          placeholder={def.description}
          className="w-full max-w-80 text-sm min-w-0"
        />
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={loading || !id}
          onClick={fetchSettings}
          className="shrink-0 whitespace-nowrap"
          title={!id ? 'Сначала введите идентификатор' : 'Запросить настройки прокачки с сервера'}
        >
          {loading ? 'Загрузка…' : 'Получить настройки'}
        </Button>
      </div>

      {result && !result.ok && (
        <div className="text-[12px] rounded-md border border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400 px-3 py-2">
          {result.error || 'Не удалось получить настройки'}
          {result.raw_preview && (
            <pre className="mt-1 whitespace-pre-wrap break-all opacity-80 text-[11px]">{result.raw_preview}</pre>
          )}
        </div>
      )}

      {result && result.ok && (
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2.5 text-[12px] max-w-2xl">
          <div className="font-semibold text-emerald-700 dark:text-emerald-400 mb-2">
            Настройки прокачки получены{result.id ? ` (id: ${result.id})` : ''}
          </div>
          {mappedEntries.length > 0 && (
            <div className="mb-2">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Применится к конфигу</div>
              <div className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5">
                {mappedEntries.map(([k, v]) => (
                  <ResultRow key={k} k={k} v={v} />
                ))}
              </div>
            </div>
          )}
          {rawEntries.length > 0 && (
            <details className="mt-1">
              <summary className="cursor-pointer text-[11px] uppercase tracking-wider text-muted-foreground select-none">
                Ответ сервера ({rawEntries.length})
              </summary>
              <div className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5 mt-1">
                {rawEntries.map(([k, v]) => (
                  <ResultRow key={k} k={k} v={v} />
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  )
}

function ResultRow({ k, v }: { k: string; v: any }) {
  const display = typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)
  return (
    <>
      <span className="font-mono text-muted-foreground">{k}</span>
      <span className="font-mono break-all text-foreground">{display === '' ? '∅' : display}</span>
    </>
  )
}

function RenderField({ name, def, value, onChange }: {
  name: string
  def: ResourceDef
  value: any
  onChange: (v: any) => void
}) {
  if (name === 'ID_SERVER_REMOTE') {
    return <RemoteSettingsField value={value} onChange={onChange} def={def} />
  }
  switch (def.type) {
    case 'Select':
      return <SelectField def={def} value={value} onChange={onChange} />

    case 'MultiCheckbox':
      return <MultiCheckboxField def={def} value={value} onChange={onChange} />

    case 'FixedInteger':
      return (
        <Input
          type="number"
          min={def.minValue}
          max={def.maxValue}
          // Empty input + placeholder so the operator can clear the field
          // freely without "010" prepending issues.
          value={value === '' || value === undefined || value === null ? '' : value}
          placeholder={String(def.defaultValue ?? '')}
          onChange={e => {
            const raw = e.target.value
            if (raw === '') {
              onChange('')
              return
            }
            let n = Number(raw)
            if (Number.isFinite(n)) {
              if (def.minValue != null && n < def.minValue) n = def.minValue
              if (def.maxValue != null && n > def.maxValue) n = def.maxValue
            }
            onChange(n)
          }}
          className="w-full max-w-28 md:w-28 tabular-nums text-sm min-w-0"
        />
      )

    case 'LinesFromFile':
      return (
        <LinesFromFileField
          value={value}
          description={def.description}
          onChange={onChange}
        />
      )

    case 'FilesFromDirectory':
      return (
        <FilesFromDirectoryField
          value={value}
          description={def.description}
          onChange={onChange}
        />
      )

    case 'FixedString':
    default:
      if ((def as any).multiline) {
        return (
          <Textarea
            value={String(value ?? '')}
            onChange={e => onChange(e.target.value)}
            placeholder={def.description}
            rows={3}
            className="w-full max-w-80 text-sm min-w-0 font-mono"
          />
        )
      }
      return (
        <Input
          value={String(value ?? '')}
          onChange={e => onChange(e.target.value)}
          placeholder={def.description}
          className="w-full max-w-80 text-sm min-w-0"
        />
      )
  }
}

function RandomIntegerField({ name, def, cfg, update }: {
  name: string
  def: ResourceDef
  cfg: Settings
  update: (patch: Partial<Settings>) => void
}) {
  const minKey = `${name}_MIN`
  const maxKey = `${name}_MAX`
  // Render "" when the value is empty/null and use placeholder for the
  // default — avoids "022" prepending when operator clears and retypes.
  const rawMin = cfg[minKey]
  const rawMax = cfg[maxKey]
  const minDisp = rawMin === '' || rawMin === undefined || rawMin === null ? '' : rawMin
  const maxDisp = rawMax === '' || rawMax === undefined || rawMax === null ? '' : rawMax
  const minPh = String(def.minValue ?? '')
  const maxPh = String(def.maxValue ?? '')

  const handleChange = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
    if (raw === '') {
      update({ [key]: '' })
      return
    }
    let n = Number(raw)
    if (Number.isFinite(n)) {
      if (def.minValue != null && n < def.minValue) n = def.minValue
      if (def.maxValue != null && n > def.maxValue) n = def.maxValue
    }
    update({ [key]: n })
  }

  return (
    <div className="flex flex-wrap items-center gap-2 min-w-0">
      <Input
        type="number"
        value={minDisp}
        placeholder={minPh}
        onChange={handleChange(minKey)}
        min={def.minValue}
        max={def.maxValue}
        className="w-full max-w-24 md:w-24 tabular-nums text-sm min-w-0"
      />
      <span className="text-xs font-medium text-muted-foreground">{'\u2014'}</span>
      <Input
        type="number"
        value={maxDisp}
        placeholder={maxPh}
        onChange={handleChange(maxKey)}
        min={def.minValue}
        max={def.maxValue}
        className="w-full max-w-24 md:w-24 tabular-nums text-sm min-w-0"
      />
    </div>
  )
}

function SubsectionHeader({ title, className }: { title: string; className?: string }) {
  return (
    <div className={`flex items-center gap-3 mt-6 pt-4 pb-3 first:mt-0 ${className ?? ''}`}>
      <span className="text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap text-muted-foreground">
        {title}
      </span>
      <Separator className="flex-1" />
    </div>
  )
}

export default function DynamicTabProfiler({ section, cfg, update }: Props) {
  const isMainSection = section === 'Основные настройки'

  const fields = getFieldsForSection(section)
  const visible = fields.filter(([key]) => {
    if (!isFieldVisible(key, cfg)) return false
    // CNT_THREADS на главной секции рендерим отдельным блоком сверху
    // (с NumberStepper для удобной прокрутки). Поэтому из общего
    // потока полей его исключаем.
    if (isMainSection && key === 'CNT_THREADS') return false
    return true
  })
  const threadsDef = isMainSection ? RESOURCES['CNT_THREADS'] : undefined

  const hasSubsections = visible.some(([, def]) => def.subsection)

  const isProxySection = section === 'Прокси и анонимность'
  const proxyOn = String(cfg['PROXY_USE'] ?? '') === 'Да'

  return (
    <div className="animate-fade-in">
      <h2 className="text-lg font-semibold mb-5 text-foreground">{section}</h2>

      {isProxySection && proxyOn && (
        <ProxyPresetsPanel cfg={cfg} update={update} />
      )}

      {isMainSection && threadsDef && (
        <div className="flex flex-col items-stretch gap-2 py-3 px-4 rounded-lg transition-colors hover:bg-accent/50 md:flex-row md:items-center md:gap-3" style={{ overflow: 'visible' }}>
          <label className="w-full text-[13px] text-left shrink-0 pt-0 leading-snug font-medium flex items-center gap-1.5 text-foreground md:w-64">
            {threadsDef.hint && <HintTooltip text={threadsDef.hint} />}
            <span>{threadsDef.description}</span>
          </label>
          <div className="min-w-0 flex-1 w-full flex items-center gap-2 flex-wrap">
            <div className="min-w-0 flex-1 min-w-[120px]">
              <NumberStepper
                def={threadsDef}
                value={cfg['CNT_THREADS']}
                onChange={v => update({ CNT_THREADS: v as unknown as string })}
              />
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2 md:space-y-1" style={{ overflow: 'visible' }}>
        {(() => {
          // Cross-subsection transitive grouping:
          // every visible field has a "root" — the topmost bool switch in its visibleWhen chain.
          // If rootOf(key) === key, it's a bool root (renders as card with children).
          // If rootOf(key) === null, it's standalone (no bool ancestor).
          // Else it's a child of some other bool — rendered inside that bool's card.
          const visibleMap = new Map<string, ResourceDef>()
          for (const [k, d] of visible) visibleMap.set(k, d)

          const rootOf = (key: string): string | null => {
            const seen = new Set<string>()
            let cur = key
            // eslint-disable-next-line no-constant-condition
            while (true) {
              if (seen.has(cur)) return null
              seen.add(cur)
              const d = visibleMap.get(cur)
              if (!d) return null
              const pvar = d.visibleWhen?.variable
              if (isSwitchResource(d)) {
                // Climb further ONLY if the parent is itself a switch (nested switches).
                // Otherwise this switch is the root — even if it depends on a plain Select.
                if (pvar) {
                  const p = visibleMap.get(pvar)
                  if (p && isSwitchResource(p)) { cur = pvar; continue }
                }
                return cur
              }
              if (pvar && visibleMap.has(pvar)) { cur = pvar; continue }
              return null
            }
          }

          // Determine subsection ownership: which root owns a subsection.
          // If all items of sub have the same root → header moves INSIDE that card.
          // Otherwise header stays outside.
          const subOwner = new Map<string | null, string | 'mixed' | null>()
          for (const [k, d] of visible) {
            const sub = d.subsection ?? null
            const r = rootOf(k)
            const cur = subOwner.get(sub)
            if (cur === undefined) subOwner.set(sub, r)
            else if (cur !== r) subOwner.set(sub, 'mixed')
          }

          const renderChildRow = (ckey: string, cdef: ResourceDef) => (
            <div key={ckey} className="flex flex-col gap-1.5 md:flex-row md:items-start md:gap-3 py-1.5">
              <label className="text-[12px] md:text-[13px] leading-snug font-medium text-foreground md:w-56 md:shrink-0 md:pt-2 flex items-center gap-1.5">
                {cdef.hint && <HintTooltip text={cdef.hint} />}
                <span>{cdef.description}</span>
              </label>
              <div className="min-w-0 flex-1">
                {cdef.type === 'RandomInteger' ? (
                  <RandomIntegerField name={ckey} def={cdef} cfg={cfg} update={update} />
                ) : (
                  <RenderField name={ckey} def={cdef} value={cfg[ckey]} onChange={v => update({ [ckey]: v })} />
                )}
              </div>
            </div>
          )

          const renderNormalRow = (key: string, def: ResourceDef) => (
            <div
              key={key}
              className="flex flex-col items-stretch gap-2 py-3 px-4 rounded-lg transition-colors hover:bg-accent/50 md:flex-row md:items-start md:gap-3"
              style={{ overflow: 'visible' }}
            >
              <label className="w-full text-[13px] text-left shrink-0 pt-0 leading-snug font-medium flex items-center gap-1.5 text-foreground md:w-64 md:pt-2">
                {def.hint && <HintTooltip text={def.hint} />}
                <span className="min-w-0">{def.description}</span>
              </label>
              <div className="min-w-0 flex-1 w-full">
                {def.type === 'RandomInteger' ? (
                  <RandomIntegerField name={key} def={def} cfg={cfg} update={update} />
                ) : (
                  <RenderField name={key} def={def} value={cfg[key]} onChange={v => update({ [key]: v })} />
                )}
              </div>
            </div>
          )

          const out: React.ReactNode[] = []
          const handled = new Set<string>()
          let externalSub: string | null | undefined = undefined

          for (const [key, def] of visible) {
            if (handled.has(key)) continue
            const root = rootOf(key)
            const sub = def.subsection ?? null
            const isSubExternal = subOwner.get(sub) === 'mixed' || subOwner.get(sub) == null

            // emit external subsection header once when it changes
            // — but only if this sub is external-owned (or mixed)
            const ownsSubInternal = (k: string | null): boolean => {
              const o = subOwner.get(k)
              return typeof o === 'string' && o !== 'mixed'
            }
            const shouldShowExternalHeader = hasSubsections && sub && (isSubExternal || (root === key))
            if (shouldShowExternalHeader && sub !== externalSub) {
              if (!ownsSubInternal(sub) || root === key) {
                out.push(<SubsectionHeader key={`sh-${sub}-${key}`} title={sub!} />)
                externalSub = sub
              }
            }

            if (root === key) {
              // Bool root — collect all children in visible order
              const kids: [string, ResourceDef][] = []
              for (const [ck, cd] of visible) {
                if (ck === key) continue
                if (rootOf(ck) === key) kids.push([ck, cd])
              }
              handled.add(key)
              for (const [ck] of kids) handled.add(ck)

              // Build inner content with subsection markers for subs owned by this bool
              const inner: React.ReactNode[] = []
              let innerSub: string | null | undefined = def.subsection ?? null
              for (const [ck, cd] of kids) {
                const csub = cd.subsection ?? null
                const owner = subOwner.get(csub)
                if (hasSubsections && csub && csub !== innerSub && owner === key) {
                  inner.push(<SubsectionHeader key={`isub-${csub}-${ck}`} title={csub} className="mt-4 mb-2" />)
                  innerSub = csub
                }
                inner.push(renderChildRow(ck, cd))
              }

              out.push(
                <BoolFieldRow
                  key={key}
                  def={def}
                  value={cfg[key]}
                  onChange={v => update({ [key]: v })}
                  hasChildren={kids.length > 0}
                >
                  {inner}
                </BoolFieldRow>
              )
              continue
            }

            if (root !== null) continue // child — handled by its root

            handled.add(key)
            if (isSwitchResource(def)) {
              out.push(
                <BoolFieldRow
                  key={key}
                  def={def}
                  value={cfg[key]}
                  onChange={v => update({ [key]: v })}
                  hasChildren={false}
                />
              )
            } else {
              out.push(renderNormalRow(key, def))
            }
          }
          return out
        })()}
      </div>

      {visible.length === 0 && !threadsDef && (
        <div className="text-sm py-8 text-center rounded-lg border border-dashed border-border text-muted-foreground">
          Нет доступных полей для текущей конфигурации
        </div>
      )}
    </div>
  )
}
